import { useCallback, useEffect, useRef, useState } from "react";
import { ANT_FormSelect } from "../styles";
import { useSelector } from "react-redux";
import { Spin } from "antd";
import { DownOutlined, LoadingOutlined } from "@ant-design/icons";

const Select = ({
  options = [],
  disabled = false,
  variant = "outlined",
  placeholder = "Select an option",
  loading = true,
  allowClear = true,
  isSelectedValueFocus = false,
  showSearch = false,
  virtual = false,
  ...rest
}) => {
  const selectRef = useRef();
  const inputSelectedRef = useRef();
  const keyHoldTimeoutRef = useRef(null);
  const inputSearchRef = useRef();
  const selectButtonRef = useRef();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchByPlaceholder, setSearchByPlaceholder] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const isRefLiistDataLoading = useSelector(
    (state) => state?.refListReducer?.isLoading
  );
  const [selectedValue, setSelectedValue] = useState(rest?.value);

  const scrollToSelected = (selectedValue) => {
    if (selectRef.current) {
      const index = options.findIndex((opt) => opt.value === selectedValue);
      if (index !== -1) {
        selectRef.current.scrollTo({ index });
      }
    }
  };
  const onDropdownVisibleChange = useCallback(
    (openState) => {
      if (isOpen !== openState) setIsOpen(openState);

      if (openState) {
        setTimeout(() => selectButtonRef.current?.focus(), 0);
      }

      setSearchQuery("");
    },
    [isOpen]
  );

  const typingBufferRef = useRef("");
  const typingTimeoutRef = useRef(null);
  const onChangeVisible = () => {
    const newState = !isOpen;
    if (isOpen !== newState) {
      onDropdownVisibleChange(newState);
      setSearchQuery("");
    }
  };

  const filteredOptions = searchQuery
    ? options.filter((option) =>
        option?.label?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      )
    : options;

  const optionItems =
    filteredOptions.length > 0
      ? filteredOptions
      : searchQuery
      ? [
          {
            label: "No options found",
            value: "No options found",
            disabled: true,
            className: "no-options-found",
            style: { color: "var(--text-blue)" },
          },
        ]
      : [];

  const onSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleSearchByPlaceholder = (e) => {
    setSearchByPlaceholder(true);
    if (typingBufferRef.current.length > 0) {
      onDropdownVisibleChange(true);
      setTimeout(() => inputSearchRef.current?.focus(), 0);
    }
  };
  const onHandleTabEnter = () => {
    const currentIndex = optionItems.findIndex(
      (opt) => opt.value === selectedValue
    );
    const option = optionItems[currentIndex];
    if (option && !option.disabled) {
      rest?.onChange?.(option.value, option);
    }
  };

  const handleTypingBuffer = (key) => {
    typingBufferRef.current += key.toLowerCase();
    clearTimeout(typingTimeoutRef.current);

    const buffer = typingBufferRef.current;
    const matches = optionItems.filter((opt) =>
      opt.label?.toLowerCase().startsWith(buffer)
    );

    if (matches.length > 0) {
      // Find current selected value in match list
      const currentIndex = matches.findIndex(
        (opt) => opt.value === selectedValue
      );

      let nextIndex = (currentIndex + 1) % matches.length;

      // Skip disabled options
      let attempts = 0;
      let nextMatch = null;

      while (attempts < matches.length) {
        const candidate = matches[nextIndex];
        if (!candidate.disabled) {
          nextMatch = candidate;
          break;
        }
        nextIndex = (nextIndex + 1) % matches.length;
        attempts++;
      }

      if (nextMatch) {
        setSelectedValue(nextMatch.value);
        rest?.onChange?.(nextMatch.value, nextMatch);
        scrollToSelected(nextMatch.value);
      }
    }

    typingTimeoutRef.current = setTimeout(() => {
      typingBufferRef.current = "";
    }, 300);
  };

  const handleTypingBufferOld = (key) => {
    typingBufferRef.current = key.toLowerCase();

    const matches = optionItems.filter((opt) =>
      opt.label?.toLowerCase().startsWith(typingBufferRef.current)
    );

    if (matches.length > 0) {
      const currentIndex = matches.findIndex(
        (opt) => opt.value === selectedValue
      );
      const nextMatch = matches[(currentIndex + 1) % matches.length];
      if (nextMatch && !nextMatch.disabled) {
        setSelectedValue(nextMatch.value);
        rest?.onChange?.(nextMatch.value, nextMatch);
        scrollToSelected(nextMatch.value);
      }
    }

    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      typingBufferRef.current = "";
    }, 1000);
  };
  const onHandleArrowKeySelect = (e) => {
    const key = e.key;
    if (keyHoldTimeoutRef.current) return;

    keyHoldTimeoutRef.current = setTimeout(() => {
      keyHoldTimeoutRef.current = null;
    }, 30);

    const currentIndex = optionItems.findIndex(
      (opt) => opt.value === selectedValue
    );
    const total = optionItems.length;

    let nextIndex = currentIndex;

    while (true) {
      if (key === "ArrowDown") {
        nextIndex += 1;
        if (nextIndex >= total) break;
      } else {
        nextIndex -= 1;
        if (nextIndex < 0) break;
      }

      const nextItem = optionItems[nextIndex];

      if (!nextItem.disabled) {
        setSelectedValue(nextItem.value);
        rest?.onChange?.(nextItem.value, nextItem);
        scrollToSelected(nextItem.value);
        break;
      }
    }

    return;
  };
  const onHandleSelectByKey = (e) => {
    const key = e.key;
    const isKeyOnHold = e.repeat;

    if (isKeyOnHold && (key === "ArrowDown" || key === "ArrowUp")) {
      e.preventDefault();
      e.stopPropagation();
      onHandleArrowKeySelect(e);
    }

    if (key === "Tab") {
      if (isOpen) {
        e.stopPropagation();
        onHandleTabEnter();
        onDropdownVisibleChange(false);
      }
      return;
    }

    if (key === "Enter") {
      if (isOpen) {
        e.stopPropagation();
        onHandleTabEnter();

        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }

        onDropdownVisibleChange(false);

        setTimeout(() => {
          selectButtonRef.current?.focus();
        }, 0);
      }
      return;
    }

    if (key === "ArrowDown" || key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      onHandleArrowKeySelect(e);
    }
    // Handle letter typing
    if (/^[a-zA-Z0-9\$]$/.test(key)) {
      handleTypingBuffer(key);
    }
  };
  useEffect(() => {}, [isRefLiistDataLoading]);

  return (
    <>
      <ANT_FormSelect
        ref={selectRef}
        disabled={disabled}
        suffixIcon={
          loading && isRefLiistDataLoading ? (
            <Spin indicator={<LoadingOutlined spin />} />
          ) : (
            <button
              type="button"
              ref={selectButtonRef}
              disabled={disabled}
              onClick={onChangeVisible}
              onMouseDown={(e) => e.preventDefault()}
              onKeyDown={(e) => onHandleSelectByKey(e)}
            >
              <DownOutlined />
            </button>
          )
        }
        onDropdownVisibleChange={onDropdownVisibleChange}
        labelRender={(item) => (
          <div
            onMouseDown={(e) => {
              if (isSelectedValueFocus) {
                e.stopPropagation();
                return false;
              }
            }}
            style={{ display: "flex", overflow: "hidden", width: "100%" }}
            title={item?.label || item?.value}
          >
            {isSelectedValueFocus ? (
              <input
                tabIndex="-1"
                type="text"
                onMouseDown={(e) => {
                  if (isSelectedValueFocus) {
                    e.stopPropagation();
                    return false;
                  }
                }}
                onKeyDown={(e) => onHandleSelectByKey(e)}
                onChange={handleSearchByPlaceholder}
                ref={inputSelectedRef}
                style={{
                  width: "100%",
                  minHeight: "30px",
                  resize: "none",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                }}
                value={item?.label || item?.value}
              />
            ) : (
              <span
                style={{
                  paddingLeft: "8px",
                  display: "inline-flex",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {item?.label || item?.value}
              </span>
            )}
          </div>
        )}
        dropdownRender={(menu) => (
          <>
            {showSearch && (
              <div className="custom-ant-select-search">
                <input
                  ref={inputSearchRef}
                  value={searchQuery}
                  readOnly={false}
                  disabled={false}
                  placeholder="Search..."
                  onChange={onSearchChange}
                  style={{
                    width: "100%",
                    minHeight: "30px",
                    resize: "none",
                    border: "none",
                    outline: "none",
                    borderRadius: "6px",
                    paddingLeft: "8px",
                    background: "#ffffff",
                  }}
                />
              </div>
            )}

            {menu}
          </>
        )}
        open={isOpen}
        showSearch={showSearch}
        allowClear={allowClear && !!rest.value}
        variant={variant}
        options={optionItems}
        className={`${rest?.className}`}
        filterOption={(input, option) =>
          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
        }
        listHeight={300}
        {...rest}
        virtua={virtual}
        placeholder={placeholder}
        notFoundContent={
          isRefLiistDataLoading ? (
            <span style={{ color: "White" }}>Loading...</span>
          ) : null
        }
      ></ANT_FormSelect>
    </>
  );
};
export default Select;
